import { MecordColors } from "Constants/Color";
import React from "react";
import { showCustomSwal } from "helpers/swalHelper";
import { useNavigate } from "react-router-dom";

interface CancelButtonProps {
  label?: string;
  icon?: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  style?: React.CSSProperties;

  confirmTitle?: string;
  confirmText?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  showConfirmation?: boolean;

  /** ✅ NEW */
  redirectTo?: string; // route path
}

const CancelButton: React.FC<CancelButtonProps> = ({
  label = "Cancel",
  icon,
  onClick,
  className = "",
  disabled = false,
  style,
  confirmTitle = "Are you sure?",
  confirmText = "Do you want to cancel? All unsaved changes will be lost.",
  confirmButtonText = "Yes, Cancel",
  cancelButtonText = "No, Continue Editing",
  showConfirmation = true,
  redirectTo,
}) => {
  const themeColor = MecordColors.ThemeBGColor;
  const navigate = useNavigate();

  const baseClasses = `btn custom-cancel-button ${className}`.trim();
  const iconClasses = icon ? `${icon} align-baseline me-1` : "";
  const combinedClasses = icon
    ? `hstack gap-2 ${baseClasses}`.trim()
    : baseClasses;

  const buttonStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderColor: themeColor,
    color: themeColor,
    borderRadius: "4px",
    padding: "8px 16px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    opacity: disabled ? 0.6 : 1,
    border: `1px solid ${themeColor}`,
    textAlign:"center",
    ...style,
  };

  const executeAction = () => {
    if (onClick) onClick();
    if (redirectTo) navigate(redirectTo) ;
  };

  const handleCancel = async () => {
    if (disabled) return;

    if (showConfirmation) {
      const result = await showCustomSwal({
        icon: "warning",
        title: confirmTitle,
        text: confirmText,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
      });

      if (result.isConfirmed) {
        executeAction();
      }
    } else {
      executeAction();
    }
  };

  return (
    <>
      <style>
        {`
          .custom-cancel-button {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
          }
          .custom-cancel-button:hover:not(:disabled) {
            opacity: 1 !important;
          }
          .custom-cancel-button:focus {
            box-shadow: 0 0 0 0.2rem ${themeColor}40 !important;
          }
          .custom-cancel-button:disabled {
            opacity: 0.6 !important;
            cursor: not-allowed !important;
          }
        `}
      </style>

      <button
        type="button"
        className={combinedClasses}
        disabled={disabled}
        style={buttonStyle}
        onClick={handleCancel}
      >
        {icon && <i className={iconClasses}></i>}
        {label}
      </button>
    </>
  );
};

export default CancelButton;
